/**
 * Mobile Menu Drawer (SCR-000)
 *
 * @requirement BRD-000 Foundational Screens
 * @screen SCR-000 Application Shell - Mobile menu drawer
 */

import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@myvault/shared/hooks';
import { Icon, type IconName } from '@myvault/shared/components';
import { cn } from '@myvault/shared/utils';
import type { UserPersona } from '@myvault/shared/types';

interface MobileMenuDrawerProps {
  open: boolean;
  onClose: () => void;
  currentPath: string;
  hasPersona: (persona: UserPersona) => boolean;
}

interface MenuItem {
  icon: IconName;
  label: string;
  href: string;
  persona?: UserPersona;
}

const menuItems: MenuItem[] = [
  { icon: 'user', label: 'Mijn profiel', href: '/settings/profile' },
  { icon: 'userGroup', label: 'Gemachtigden', href: '/delegates', persona: 'retail_consumer' },
  { icon: 'share', label: 'Gedeelde Kluizen', href: '/gemachtigde/vaults', persona: 'delegate' },
  { icon: 'chartBar', label: 'Accountant Dashboard', href: '/professional', persona: 'professional_accountant' },
  { icon: 'buildingOffice', label: 'Cliënten', href: '/professional/clients', persona: 'professional_accountant' },
  { icon: 'chartBar', label: 'Advies Dashboard', href: '/advisor', persona: 'intermediary_advisor' },
  { icon: 'users', label: 'Mijn Cliënten', href: '/advisor/clients', persona: 'intermediary_advisor' },
  { icon: 'calendarDays', label: 'Verlengingen', href: '/advisor/renewals', persona: 'intermediary_advisor' },
  { icon: 'currencyEuro', label: 'Provisie', href: '/advisor/commissions', persona: 'intermediary_advisor' },
  { icon: 'cog', label: 'Instellingen', href: '/settings' },
  { icon: 'creditCard', label: 'Abonnement', href: '/subscription' },
  { icon: 'questionMarkCircle', label: 'Help', href: '/help' },
];

export function MobileMenuDrawer({ open, onClose, currentPath, hasPersona }: MobileMenuDrawerProps) {
  const { user, logout } = useAuth();

  // Lock body scroll when open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  const filteredItems = menuItems.filter((item) => !item.persona || hasPersona(item.persona));

  const initials = user?.displayName
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) || 'U';

  return (
    <>
      {/* Backdrop */}
      <div
        className={cn(
          'fixed inset-0 z-50 bg-black/50 transition-opacity lg:hidden',
          open ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        className={cn(
          'fixed inset-y-0 left-0 z-50 w-72 transform bg-white transition-transform duration-200 lg:hidden',
          open ? 'translate-x-0' : '-translate-x-full'
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Menu"
      >
        {/* Header */}
        <div className="flex h-16 items-center justify-between border-b border-gray-100 px-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500 text-sm font-medium text-blue-900">
              {initials}
            </div>
            <div>
              <p className="font-medium text-gray-900">{user?.displayName || 'Gebruiker'}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded p-2 text-gray-500 hover:bg-gray-100"
            aria-label="Sluit menu"
          >
            <Icon name="close" variant="outline" size="md" />
          </button>
        </div>

        {/* Menu items */}
        <nav className="flex-1 overflow-y-auto py-4">
          <div className="space-y-1 px-3">
            {filteredItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                onClick={onClose}
                className={cn(
                  'flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium',
                  currentPath.startsWith(item.href)
                    ? 'bg-blue-100 text-blue-900'
                    : 'text-gray-700 hover:bg-gray-100'
                )}
              >
                <Icon name={item.icon} variant="outline" size="md" />
                {item.label}
              </Link>
            ))}
          </div>

          <div className="my-4 border-t border-gray-100" />

          {/* Theme toggle */}
          <div className="px-3">
            <div className="flex items-center justify-between rounded-lg px-3 py-2.5">
              <span className="flex items-center gap-3 text-sm font-medium text-gray-700">
                <Icon name="eye" variant="outline" size="md" />
                Donkere modus
              </span>
              <button
                className="relative h-6 w-11 rounded-full bg-gray-200 transition-colors"
                aria-label="Schakel donkere modus"
              >
                <span className="absolute left-0.5 top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform" />
              </button>
            </div>
          </div>

          <div className="my-4 border-t border-gray-100" />

          {/* Logout */}
          <div className="px-3">
            <button
              onClick={() => {
                onClose();
                logout();
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium text-red-600 hover:bg-red-50"
            >
              <Icon name="arrowRight" variant="outline" size="md" />
              Uitloggen
            </button>
          </div>
        </nav>
      </div>
    </>
  );
}
